import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Select } from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import { ErrorState } from '@/shared/feedback';
import { toError } from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import type { TenantAiSettings } from '@/api/schemas/ai';

/**
 * The tenant's OWN AI settings: where its configuration comes from, and
 * whether it may fall back to the platform's provider.
 *
 * ---------------------------------------------------------------------------
 * THE FALLBACK SWITCH SPENDS THE PLATFORM'S MONEY
 * ---------------------------------------------------------------------------
 * A platform fallback means the platform calls the provider on the tenant's
 * behalf and the platform's allowance pays for it. That is why the switch
 * says so in its own description, and why it is OFF until the tenant turns it
 * on. It is not a convenience toggle; it is a spending decision.
 *
 * ---------------------------------------------------------------------------
 * THE CONTROL SHOWS THE SERVER'S COPY
 * ---------------------------------------------------------------------------
 * The control answers immediately while the write is in flight, so the
 * selection is kept as a DRAFT — a draft made against the copy the server
 * held. The draft is dropped the moment the server's copy moves on, so the
 * control cannot show a selection the server does not hold: if the platform
 * changes this tenant's source, the switch follows the platform.
 *
 * The draft is NOT re-seeded by a remount either. A remount would tear the
 * control down while a person has it open — and a select that closes under
 * the pointer is a select that loses the change the person was making.
 */
export type TenantOwnSettingsProps = {
  settings: TenantAiSettings;
  onSaved: () => void;
};

/** What the person is choosing, and the server's copy it was chosen against. */
type SettingsDraft = {
  readonly configSource: TenantAiSettings['configSource'];
  readonly allowPlatformFallback: boolean;
  /** The server's copy the draft was made against. */
  readonly base: TenantAiSettings['configSource'];
};

/**
 * The selection the person is making, kept against the copy it was made from.
 *
 * A draft made against a copy the server no longer holds is not the person's current
 * intention — it is a stale note. The server's copy wins, so the control cannot show a
 * selection the server does not hold: if the platform changes this tenant's source, the
 * switch follows the platform.
 */
function useSettingsDraft(settings: TenantAiSettings): {
  readonly configSource: TenantAiSettings['configSource'];
  readonly allowPlatformFallback: boolean;
  readonly choose: (next: {
    readonly configSource?: TenantAiSettings['configSource'];
    readonly allowPlatformFallback?: boolean;
  }) => void;
} {
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const live = draft !== null && draft.base === settings.configSource;

  return {
    configSource: live ? draft.configSource : settings.configSource,
    allowPlatformFallback: live ? draft.allowPlatformFallback : settings.allowPlatformFallback,
    choose: (next) => {
      setDraft({
        configSource: next.configSource ?? (live ? draft.configSource : settings.configSource),
        allowPlatformFallback:
          next.allowPlatformFallback ??
          (live ? draft.allowPlatformFallback : settings.allowPlatformFallback),
        base: settings.configSource,
      });
    },
  };
}

export function TenantOwnSettings({ settings, onSaved }: TenantOwnSettingsProps): ReactNode {
  const draft = useSettingsDraft(settings);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await aiApi.updateEffectiveSettings({
        configSource: draft.configSource,
        allowPlatformFallback: draft.allowPlatformFallback,
      });
      onSaved();
    } catch (cause) {
      setSaveError(toError(cause));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {saveError ? (
        <ErrorState title="Could not save your AI settings" message={saveError.message} />
      ) : null}

      <section className="space-y-4 rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium">Where your AI configuration comes from</h3>

        <Select
          label="Configuration source"
          value={draft.configSource}
          onChange={(event) => {
            draft.choose({
              configSource: event.target.value === 'tenant_owned' ? 'tenant_owned' : 'platform_default',
            });
          }}
          options={[
            { value: 'platform_default', label: 'Platform default' },
            { value: 'tenant_owned', label: 'Tenant-owned provider' },
          ]}
          hint={
            settings.allowTenantOwnedProvider
              ? 'Platform default uses the platform’s provider and the platform’s allowance. Tenant-owned uses the provider you configure below.'
              : 'Your workspace uses the platform’s provider. The platform does not currently allow a provider of your own here.'
          }
        />

        <Switch
          label="Fall back to the platform provider"
          description="Off by default. When on, and your own provider is missing or unavailable, the platform calls its provider on your behalf — and the platform's allowance pays for it."
          checked={draft.allowPlatformFallback}
          onCheckedChange={(checked) => {
            draft.choose({ allowPlatformFallback: checked });
          }}
        />
      </section>

      <Button loading={isSaving} onClick={() => { void handleSave(); }}>
        Save AI settings
      </Button>
    </div>
  );
}
